import { FC, PropsWithChildren, useMemo } from 'react';
import { Flex } from 'antd';
import {
  getConfidentialDataObjectLabel,
  getDatePeriodString,
  getSortViewPermissionsByDataObject,
  getUserViewPermissionType,
  sortViewPermissionsByType,
  sortViewPermissionsByValidPeriod,
  Table,
  TextEllipsis,
  USER_VIEW_PERMISSION_DEFAULT_SORT,
  useConfidentialDataObjectsQuery,
  UserViewPermissionFormDataType,
  UserViewPermissionTypeName,
  useUserFormLabelTranslation,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserViewPermissionsTable.translations.json';
import { createColumnHelper } from '@tanstack/react-table';

const { addTranslations } = i18nNsFactory('user-view-permissions-table');
addTranslations(translations);

const columnHelper = createColumnHelper<UserViewPermissionFormDataType>();

export interface UserViewPermissionsTableProps {
  userViewPermissions: UserViewPermissionFormDataType[];
}

export const UserViewPermissionsTable: FC<
  PropsWithChildren<UserViewPermissionsTableProps>
> = (props) => {
  const { userViewPermissions } = props;
  const { t } = useUserFormLabelTranslation();
  const { data: confidentialDataObjects, isPending } =
    useConfidentialDataObjectsQuery();

  const columns = useMemo(
    () => [
      columnHelper.accessor('type' as any, {
        header: t('viewPermissions.type.label'),
        meta: { width: 70 },
        enableColumnFilter: false,
        cell: (props) => (
          <UserViewPermissionTypeName
            type={getUserViewPermissionType(props.row.original.id)}
          />
        ),
        sortingFn: sortViewPermissionsByType,
      }),
      columnHelper.accessor('id', {
        header: t('viewPermissions.id.label'),
        enableColumnFilter: false,
        cell: (props) => (
          <TextEllipsis>
            {getConfidentialDataObjectLabel(
              confidentialDataObjects,
              props.row.original.id,
            )}
          </TextEllipsis>
        ),
        sortingFn: getSortViewPermissionsByDataObject(confidentialDataObjects),
      }),
      columnHelper.accessor('validPeriod', {
        header: t('viewPermissions.validPeriod.label'),
        meta: { width: 180 },
        enableColumnFilter: false,
        cell: (props) => (
          <TextEllipsis>
            {getDatePeriodString(
              props.renderValue()?.[0],
              props.renderValue()?.[1],
            )}
          </TextEllipsis>
        ),
        sortingFn: sortViewPermissionsByValidPeriod,
      }),
    ],
    [confidentialDataObjects, t],
  );

  if (isPending) {
    return null;
  }

  return (
    <Flex align="end" gap="middle" vertical>
      <Table
        name="viewPermissions"
        data={userViewPermissions}
        columns={columns}
        defaultSort={USER_VIEW_PERMISSION_DEFAULT_SORT}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </Flex>
  );
};
